const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

export class ApiError extends Error {
  readonly status: number;
  readonly details: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

function readCookie(name: string): string | null {
  const prefix = `${encodeURIComponent(name)}=`;
  const cookie = document.cookie.split('; ').find((item) => item.startsWith(prefix));
  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null;
}

function readErrorMessage(payload: unknown): string {
  if (
    typeof payload === 'object'
    && payload !== null
    && 'message' in payload
    && typeof payload.message === 'string'
  ) {
    return payload.message;
  }

  return 'The request could not be completed.';
}

interface RequestOptions extends RequestInit {
  expectNoContent?: boolean;
}

interface NoContentRequestOptions extends RequestOptions {
  expectNoContent: true;
}

export function request(path: string, options: NoContentRequestOptions): Promise<void>;
export function request<T>(path: string, options?: RequestInit): Promise<T>;
export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T | void> {
  const { expectNoContent: _expectNoContent, ...fetchOptions } = options;
  const headers = new Headers(fetchOptions.headers);
  headers.set('Accept', 'application/json');

  if (fetchOptions.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const method = (fetchOptions.method || 'GET').toUpperCase();
  const csrfToken = readCookie('XSRF-TOKEN');
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method) && csrfToken) {
    headers.set('X-XSRF-TOKEN', csrfToken);
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...fetchOptions,
    headers,
    credentials: 'include',
  });

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload: unknown = isJson ? await response.json() : null;

  if (!response.ok) {
    throw new ApiError(readErrorMessage(payload), response.status, payload);
  }

  if (response.status === 204) {
    return;
  }

  return payload as T;
}

export function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}
